//Tracking users
import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  onSnapshot, 
  query, 
  where,
  serverTimestamp,
  Timestamp,
  getDocs,
  writeBatch,
  QueryDocumentSnapshot,
  deleteDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { UserPresence } from '../types';
import { DEFAULT_COLOR_KEY } from '../utils/colors';
import { onDisconnect, getDatabase, ref, set } from 'firebase/database';
import { tracedOnSnapshot } from './firestoreDebug';

interface UserPresenceData {
  userId: string;
  username: string;
  initials: string;
  position: { x: number; y: number };
  lastUpdate: Timestamp;
  isActive: boolean;
  preferredColor?: string;
}

//Init w/all user fields
export const initializeUserPresence = async (
  forestId: string,
  userId: string,
  username: string,
  initials: string,
  position: { x: number; y: number },
  preferredColor?: string
): Promise<void> => {
  try {
    const userPresenceRef = doc(db, 'forests', forestId, 'userPresence', userId);
    
    await setDoc(userPresenceRef, {
      userId,
      username,
      initials,
      position,
      preferredColor: preferredColor || DEFAULT_COLOR_KEY,
      lastUpdate: serverTimestamp(),
      isActive: true
    });
    
    console.log(`Initialized user presence for ${username} at (${position.x}, ${position.y})`);
  } catch (error) {
    console.error('Error initializing user presence:', error);
    throw error;
  }
};

//Update position
export const updateUserPosition = async (
  forestId: string,
  userId: string,
  position: { x: number; y: number }
): Promise<void> => {
  try {
    const userPresenceRef = doc(db, 'forests', forestId, 'userPresence', userId);
    
    //Update pos and time
    await updateDoc(userPresenceRef, {
      'position.x': position.x,
      'position.y': position.y,
      lastUpdate: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user position:', error);
    throw error;
  }
};

//Disconnect ghost on idle
export const setupPresenceSystem = async (forestId: string, userId: string) => {
  const rtdb = getDatabase();
  const presenceRef = ref(rtdb, `presence/${forestId}/${userId}`);
  
  //disconnect on DB idle
  await onDisconnect(presenceRef).remove();
  
  //Set presence
  await set(presenceRef, {
    online: true,
    lastSeen: serverTimestamp()
  });
};

//Update other fields on change
export const updateUserPresence = async (
  forestId: string,
  userId: string,
  updates: {
    username?: string;
    initials?: string;
    preferredColor?: string;
    isActive?: boolean;
  }
): Promise<void> => {
  try {
    const userPresenceRef = doc(db, 'forests', forestId, 'userPresence', userId);
    
    const updateData: any = {
      lastUpdate: serverTimestamp()
    };
    
    if (updates.username !== undefined) updateData.username = updates.username;
    if (updates.initials !== undefined) updateData.initials = updates.initials;
    if (updates.preferredColor !== undefined) updateData.preferredColor = updates.preferredColor;
    if (updates.isActive !== undefined) updateData.isActive = updates.isActive;
    
    await updateDoc(userPresenceRef, updateData);
    
    console.log(`Updated user presence fields for ${userId}:`, Object.keys(updates));
  } catch (error) {
    console.error('Error updating user presence:', error);
    throw error;
  }
};

//Subscribe to ALL user positions in forest
//ToDo: Test heavily for scale

export const subscribeToUserPresence = (
  forestId: string,
  currentUserId: string,
  onUpdate: (users: UserPresence[]) => void,
  onError: (error: Error) => void
): (() => void) => {
  try {
    console.log('Setting up user presence subscription for forest:', forestId);
    
    const userPresenceCollection = collection(db, 'forests', forestId, 'userPresence');
    const q = query(
      userPresenceCollection,
      where('isActive', '==', true)
    );
    /*
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        console.log('User presence update - total users:', querySnapshot.docs.length);
        
        const users: UserPresence[] = querySnapshot.docs
          .map(doc => {
            const data = doc.data() as UserPresenceData;
            return {
              userId: data.userId,
              username: data.username,
              initials: data.initials,
              position: data.position,
              lastUpdate: data.lastUpdate?.toDate() || new Date(),
              isActive: data.isActive,
              preferredColor: data.preferredColor || DEFAULT_COLOR_KEY
            };
          })
          //Exclude current user from ghost list
          .filter(user => user.userId !== currentUserId)
          //Filter out stale users
          .filter(user => {
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000); //SETTING: 5 min user window
            return user.lastUpdate > fiveMinutesAgo;
          });
        
        console.log(`Active ghost users: ${users.length}`);
        console.log('Ghost positions:', users.map(u => `${u.username}(${u.position.x},${u.position.y})`));
        
        onUpdate(users);
      },
      (error) => {
        console.error('User presence subscription error:', error);
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    );
    
*/

    /*ToDelete*/
    const unsubscribe = tracedOnSnapshot(
  'presence',
  `forests/${forestId}/userPresence`,
  q,
  (querySnapshot) => {
    console.log('User presence update - total users:', querySnapshot.docs.length);
    const users: UserPresence[] = querySnapshot.docs
      .map(doc => {
        const data = doc.data() as UserPresenceData;
        return {
          userId: data.userId,
          username: data.username,
          initials: data.initials,
          position: data.position,
          lastUpdate: data.lastUpdate?.toDate() || new Date(),
          isActive: data.isActive,
          preferredColor: data.preferredColor || DEFAULT_COLOR_KEY
        };
      })
      .filter(user => user.userId !== currentUserId)
      .filter(user => {
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        return user.lastUpdate > fiveMinutesAgo;
      });
    
    console.log(`Active ghost users: ${users.length}`);
    onUpdate(users);
  },
  (error) => {
    console.error('User presence listener error:', error);
    onError(error);
  }
);
/* ToDo: End ToDelete*/


    return unsubscribe;
  } catch (error) {
    console.error('Error setting up user presence subscription:', error);
    onError(error instanceof Error ? error : new Error(String(error)));
    return () => {};
  }
};

//Mark user as inactive when leaving
export const setUserInactive = async (
  forestId: string,
  userId: string
): Promise<void> => {
  try {
    const userPresenceRef = doc(db, 'forests', forestId, 'userPresence', userId);
    
    await updateDoc(userPresenceRef, {
      isActive: false,
      lastUpdate: serverTimestamp()
    });
    
    console.log('User marked as inactive:', userId);
  } catch (error) {
    console.error('Error marking user inactive:', error);
  }
};

//Clean up users
export const removeUserPresence = async (
  forestId: string,
  userId: string
): Promise<void> => {
  try {
    const userPresenceRef = doc(db, 'forests', forestId, 'userPresence', userId);
    await deleteDoc(userPresenceRef);
    
    console.log('User presence removed:', userId);
  } catch (error) {
    console.error('Error removing user presence:', error);
  }
};

//Clean up inactive users
export const cleanupInactiveUsers = async (forestId: string): Promise<void> => {
  try {
    const userPresenceCollection = collection(db, 'forests', forestId, 'userPresence');
    const snapshot = await getDocs(userPresenceCollection);
    
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const batch = writeBatch(db);
    let cleanupCount = 0;
    
    snapshot.docs.forEach((doc: QueryDocumentSnapshot) => {
      const data = doc.data() as UserPresenceData;
      const lastUpdate = data.lastUpdate.toDate();
      
      if (lastUpdate < fiveMinutesAgo) {
        batch.delete(doc.ref);
        cleanupCount++;
      }
    });
    
    if (cleanupCount > 0) {
      await batch.commit();
      console.log(`Cleaned up ${cleanupCount} inactive users`);
    }
  } catch (error) {
    console.error('Error cleaning up inactive users:', error);
  }
};